import React, {useState} from 'react';
import styled from 'styled-components';
import GenericModal from '../../../containers/modal/generic_modal';
import {BUTTON_COLOR, BUTTON_STYLE, MODULE_TYPES} from '../../../constants';
import CustomScrollDiv from '../../shared/components/scroll-bars/custom_scroll_div';
import {BasicTextInput, Checkbox, InputLabel, Table} from '@/web-components';
import {cloneDeep, uniq} from 'lodash';
import {useIntl} from 'react-intl';
import {createToast} from '../../shared/components/toasts/toast';
import Util from '../../shared/util/util';
import UpdateProfileMutation from '../../../mutations/UpdateProfileMutation';
import {TextBold} from '../../../Text.styled';
import ForecastTooltip from '../../shared/components/tooltips/ForecastTooltip';
import {
	dependencyChecked,
	getDefaultPermissions,
	getPermissionsAfterRemoving,
	getPermissionsOutsideGroups,
	hasPermissionGroup,
} from '../../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {hasModule} from '../../shared/util/ModuleUtil';
import {ModalText, ModalWrapper} from '../../../containers/modal/modal_styled';
import {ExpansionToggle} from '../../shared/components/task-tables/task-table-v3/elements/Elements.styled';

const InputWrapper = styled.div`
	margin-left: 16px;
`;

// Drawn unticked when the caller asks for it. The Contributor profile holds these so that
// enforcing task permissions by profile does not strip task access from everyone on it, but an
// admin cannot change them here — the restriction toggle on the profiles page governs them instead.
// Display only: a save still writes the set the profile holds.
//
// Deliberately NOT the same set as the one the Collaborator-seat restriction withholds, which is
// `COLLABORATOR_SEAT_RESTRICTED_PERMISSIONS` in `AuthorizationLogic` — this list minus
// TASK_STATUS_CHANGE, three members to this one's four (spec 002). Status is drawn unticked here
// because Contributor is read-only and a lone ticked row among four reads as a grant an admin can
// act on — but it stays granted, and a restricted seat keeps moving status. Adding it to the
// backend's withheld set to make the two match would take that away from every company that
// already enabled the toggle: spec 004 Decision 2, as amended after functional review. That set is
// pinned by `AuthorizationLogicTest.it_keeps_status_change_for_a_restricted_contributor`, which is
// what turns red if the two are ever reconciled from the wrong end.
const TASK_PERMISSIONS = [
	PERMISSION_TYPE.TASK_CREATE,
	PERMISSION_TYPE.TASK_UPDATE,
	PERMISSION_TYPE.TASK_DELETE,
	PERMISSION_TYPE.TASK_STATUS_CHANGE,
];

// A permission name sits 40px in: the first column starts at a 16px inset, then the expansion
// toggle takes 24px. A nested row adds NestedIndent's 24px on top of that, so its name starts
// at 64px. Matching those puts the description's first character directly under the name it
// belongs to instead of hanging to the left of it.
const DESCRIPTION_INSET = 40;
const NESTED_DESCRIPTION_INSET = 64;

// Deliberately not a Table.Row: table rows are locked to a per-level pixel height and clip
// their overflow, so a description longer than one line was cropped top and bottom. As a plain
// block the description grows to whatever its text needs.
const Description = styled.div`
	padding: 8px 16px 12px ${({nested}) => (nested ? NESTED_DESCRIPTION_INSET : DESCRIPTION_INSET)}px;
	border-bottom: 1px solid #e6e6e6;
	white-space: normal;
	font-size: 12px;
	font-weight: 500;
	line-height: 1.5;
	color: #a5a5a5;
`;

// Indents a permission row beneath the container row above it. Rendered as a leading spacer
// rather than padding on the cell so rows without a parent are laid out exactly as before.
const NestedIndent = styled.span`
	display: inline-block;
	width: 24px;
`;

const ErrorMessage = styled.div`
	color: #d0021b;
	font-size: 13px;
	padding-left: 16px;
	padding-top: 4px;
`;

const profileModal = props => {
	const [_name, setName] = useState(props.profile.name);
	const [_error, setError] = useState(null);
	const [_permissions, setPermissions] = useState(cloneDeep(props.profile.permissions));

	const intl = useIntl();

	const permissionChecked = (rowData, removing) => {
		if (removing) {
			setPermissions(getPermissionsAfterRemoving(rowData, _permissions));
		} else {
			const defaultPermissions = getDefaultPermissions(rowData);
			const addedPermissions = uniq(_permissions.concat(rowData.permissions).concat(defaultPermissions));
			setPermissions(addedPermissions);
		}
	};

	const handleSetName = name => {
		setName(name);
		if (name.length > 191) {
			setError(intl.formatMessage({id: 'settings_finance.name_limit_191'}));
		} else {
			setError(null);
		}
	};

	const handleSave = () => {
		const onSuccess = () => {
			createToast({
				duration: 5000,
				message: intl.formatMessage({id: 'settings.profile_updated'}),
			});
		};

		Util.CommitMutation(
			UpdateProfileMutation,
			{
				id: props.profile.id,
				name: _name,
				profilePermissions: _permissions,
			},
			onSuccess
		);
	};

	const shownAsGranted = props.untickTaskPermissions
		? _permissions.filter(permission => !TASK_PERMISSIONS.includes(permission))
		: _permissions;

	const getPermissions = () => {
		return props.permissions.filter(group => {
			// Only show sisense groups for people with sisense module
			if (group.permissions.includes(PERMISSION_TYPE.SISENSE_READ)) {
				return hasModule(MODULE_TYPES.SISENSE);
			}
			return true;
		});
	};

	// Granted permissions no fully held group accounts for, which would otherwise sit under an
	// unticked row that can never clear them. Revoke-only: unticking clears the set, and nothing
	// can be added back from here. Judged on what the profile really holds, not what is drawn.
	const permissionsOutsideGroups = getPermissionsOutsideGroups(_permissions);
	const outsideGroupsRow =
		permissionsOutsideGroups.length > 0
			? {
					name: intl.formatMessage(
						{id: 'settings.profile_permissions_outside_groups'},
						{count: permissionsOutsideGroups.length}
					),
					permissions: permissionsOutsideGroups,
					description: permissionsOutsideGroups.join(', '),
			  }
			: null;

	const content = (
		<ModalWrapper large>
			{!props.hideProfileName && (
				<InputWrapper>
					<InputLabel
						text={'Profile name'}
						activeError={!!_error}
						extension={_error}
						child={
							props.profileNameCanBeEdited ? (
								<BasicTextInput
									value={_name}
									onChange={handleSetName}
									hasError={_error}
									width="250px"
									placeholder={'Profile name'}
								/>
							) : (
								<TextBold>{_name}</TextBold>
							)
						}
					/>
				</InputWrapper>
			)}
			<ModalText>{props.description}</ModalText>
			{_error && <ErrorMessage>{_error}</ErrorMessage>}
			<CustomScrollDiv style={{height: '50vh', minWidth: '500px'}}>
				<Table>
					<Table.Header>
						<Table.HeaderColumn flex={1} usePadding visible width="MEDIUM">
							<Table.HeaderColumn.Title>Permission</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
						<Table.HeaderColumn usePadding visible width="MEDIUM" align="center">
							<Table.HeaderColumn.Title>Enabled</Table.HeaderColumn.Title>
						</Table.HeaderColumn>
					</Table.Header>
					<Table.Rows
						data={{
							rows: outsideGroupsRow ? [...getPermissions(), outsideGroupsRow] : getPermissions(),
						}}
					>
						{({rowData, tableColumnsProps}) => {
							const isChecked = hasPermissionGroup(rowData, shownAsGranted);
							const canBeEdited = props.profileCanBeEdited;
							const isDisabled = !canBeEdited || dependencyChecked(rowData, _permissions);

							return (
								<>
									<Table.Row
										hoverStyle={{
											color: 'blue',
										}}
										{...tableColumnsProps}
									>
										<Table.Column>
											{rowData.nested && <NestedIndent />}
											{rowData.description && (
												<ExpansionToggle
													expanded={tableColumnsProps.expanded}
													onClick={() => tableColumnsProps.toggleExpansion()}
												/>
											)}
											{rowData.name}
										</Table.Column>
										<Table.Column align="center">
											<ForecastTooltip
												content={
													isDisabled &&
													(!canBeEdited
														? 'You are not allowed to edit this permission'
														: 'This permission cannot be disabled as another permission depends on it')
												}
											>
												<Checkbox
													disabled={isDisabled}
													checked={isChecked}
													onChange={() => permissionChecked(rowData, isChecked)}
												/>
											</ForecastTooltip>
										</Table.Column>
									</Table.Row>
									{tableColumnsProps.expanded && (
										<Description nested={rowData.nested}>{rowData.description}</Description>
									)}
								</>
							);
						}}
					</Table.Rows>
				</Table>
			</CustomScrollDiv>
		</ModalWrapper>
	);
	return (
		<GenericModal
			buttons={[
				{
					text: 'Cancel',
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: 'Save',
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					disabled: !props.profileCanBeEdited || !!_error,
					callback: () => handleSave(),
				},
			]}
			headerText={props.headerText}
			wrapperClassName="edit-profile-modal"
			closeModal={props.closeModal}
			content={content}
		/>
	);
};

export default profileModal;
